import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Truck,
  FileText,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  PauseCircle,
} from 'lucide-react';
import { DashboardSummary } from '../../types/dashboard';

interface Props {
  summary: DashboardSummary;
  loading?: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

const formatPercent = (value: number) => {
  return value.toFixed(1) + '%';
};

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  subtitle?: string;
  warning?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color,
  bgColor,
  subtitle,
  warning,
}) => (
  <div className={`card p-4 ${warning ? 'border-l-4 border-yellow-500' : ''}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className={`text-xl font-bold ${color}`}>{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-full ${bgColor}`}>{icon}</div>
    </div>
  </div>
);

const LoadingSkeleton: React.FC = () => (
  <div className="card p-4 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <div className="h-4 w-24 bg-gray-200 rounded"></div>
        <div className="h-6 w-32 bg-gray-200 rounded"></div>
      </div>
      <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
    </div>
  </div>
);

export const DashboardStats: React.FC<Props> = ({ summary, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <LoadingSkeleton key={i} />
        ))}
      </div>
    );
  }

  const stats: StatCardProps[] = [
    {
      title: t('dashboard.totalRevenue'),
      value: formatCurrency(summary.totalRevenue) + 'đ',
      icon: <DollarSign className="w-6 h-6 text-blue-600" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: t('dashboard.totalExpenses'),
      value: formatCurrency(summary.totalExpenses) + 'đ',
      icon: <TrendingDown className="w-6 h-6 text-orange-600" />,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
    {
      title: t('dashboard.totalProfit'),
      value: formatCurrency(summary.totalProfit) + 'đ',
      icon:
        summary.totalProfit >= 0 ? (
          <TrendingUp className="w-6 h-6 text-green-600" />
        ) : (
          <TrendingDown className="w-6 h-6 text-red-600" />
        ),
      color: summary.totalProfit >= 0 ? 'text-green-600' : 'text-red-600',
      bgColor: summary.totalProfit >= 0 ? 'bg-green-100' : 'bg-red-100',
      subtitle: formatPercent(summary.profitMargin) + ' ' + t('dashboard.margin'),
    },
    {
      title: t('dashboard.outstandingDebt'),
      value: formatCurrency(summary.totalOutstandingDebt) + 'đ',
      icon: <CreditCard className="w-6 h-6 text-red-600" />,
      color: summary.totalOutstandingDebt > 0 ? 'text-red-600' : 'text-gray-600',
      bgColor: summary.totalOutstandingDebt > 0 ? 'bg-red-100' : 'bg-gray-100',
      subtitle: `NCC: ${formatCurrency(summary.supplierDebt)}đ | KH: ${formatCurrency(summary.customerDebt)}đ`,
      warning: summary.totalOutstandingDebt > 0,
    },
    {
      title: t('dashboard.completedTrips'),
      value: summary.completedTripsCount.toString(),
      icon: <CheckCircle className="w-6 h-6 text-green-600" />,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      subtitle: summary.activeTripsCount + ' ' + t('dashboard.activeTrips'),
    },
    {
      title: t('dashboard.activeVehicles'),
      value: summary.activeVehiclesCount.toString(),
      icon: <Truck className="w-6 h-6 text-blue-600" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: t('dashboard.idleVehicles'),
      value: summary.idleVehiclesCount.toString(),
      icon: <PauseCircle className="w-6 h-6 text-gray-600" />,
      color: summary.idleVehiclesCount > 0 ? 'text-yellow-600' : 'text-gray-600',
      bgColor: summary.idleVehiclesCount > 0 ? 'bg-yellow-100' : 'bg-gray-100',
      warning: summary.idleVehiclesCount > 0,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  );
};
